import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import BlogCard from '../components/BlogCard';
import Newsletter from '../components/Newsletter';
import AnimatedPage from '../components/AnimatedPage';
import { ArrowRight, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import SEOHead from '../components/SEOHead';

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      let q;

      if (selectedCategory === 'all') {
        q = query(
          collection(db, 'posts'),
          orderBy('createdAt', 'desc'),
          limit(12)
        );
      } else {
        q = query(
          collection(db, 'posts'),
          where('category', '==', selectedCategory),
          orderBy('createdAt', 'desc'),
          limit(12)
        );
      }

      const querySnapshot = await getDocs(q);
      const postsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPosts(postsData);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'posts'));
      const uniqueCategories = [
        ...new Set(querySnapshot.docs.map((doc) => doc.data().category).filter(Boolean)),
      ];
      setCategories(['all', ...uniqueCategories]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  return (
    <AnimatedPage>
      <SEOHead />
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 px-4 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="inline-flex items-center mb-6 px-4 py-2 bg-black text-white rounded-full"
              >
                <Sparkles size={16} className="mr-2" />
                <span className="text-sm font-body font-medium">Welcome to RaiBlog</span>
              </motion.div>

              <h1 className="heading-1 mb-6">
                Stories That
                <br />
                <span className="relative inline-block">
                  Inspire & Inform
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                    className="absolute bottom-2 left-0 h-3 bg-black opacity-20 -z-10"
                  />
                </span>
              </h1>

              <p className="body-text text-gray-600 max-w-2xl mx-auto mb-8">
                Explore insightful articles, thought-provoking stories, and creative
                perspectives from around the world.
              </p>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.scrollTo({ top: 600, behavior: 'smooth' })}
                className="inline-flex items-center px-8 py-4 bg-black text-white rounded-lg font-body font-semibold hover:bg-gray-800 transition-colors"
              >
                Explore Articles
                <ArrowRight size={20} className="ml-2" />
              </motion.button>
            </motion.div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-black opacity-5 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-black opacity-5 rounded-full blur-3xl -z-10" />
        </section>

        {/* Category Filter */}
        <section className="py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-wrap justify-center gap-3"
            >
              {categories.map((category) => (
                <motion.button
                  key={category}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full font-body font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </motion.button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="shimmer h-96 rounded-lg" />
                ))}
              </div>
            ) : posts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, index) => (
                  <BlogCard key={post.id} post={post} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-600 text-lg font-body">
                  No posts found in this category.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <Newsletter />
          </div>
        </section>
      </div>
    </AnimatedPage>
  );
};

export default Home;
